import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, BarChart3, Building2, CalendarClock, CalendarDays, Check, ChevronDown, ChevronLeft, Copy, Download, ExternalLink,
  EyeOff, Globe, Hourglass, Info, Loader2, Mail, MessagesSquare, RefreshCw, School, Send, ShieldCheck, StickyNote, Trash2, Upload, UserPlus, Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { MailWerkstatt } from '../../components/Zusammenarbeit/MailWerkstatt';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import { Avatar } from '../../components/Planung/Avatar';
import {
  BrevoChip, EmpfaengerChip, EventChip, SportschulChips, StandBadge, StatistikZeile, VersandChip,
} from '../../components/Planung/PlanungsChips';
import {
  ANTWORT_INFO, STAND_INFO, VERSAND_INFO, alsTag, brevoStatusText, datumKurz, datumZeit, empfaengerText, feedbackEntscheiden,
  feedbackGelesen, feedbackLink, feedbackSenden, inZwischenablage, inhaltAendern, ladePlanungMail, ladePlanungVersion,
  nachBrevoUebertragen, prozent, tageText, versionWiederherstellen, wannText, zeitraumText,
  type PlanungsDetail, type SichtbarFuer, type WerkstattAdapter,
} from '../../lib/planung';
import { edgeFunctions, type BrevoDraftResult } from '../../lib/supabase';
import { istRedaktion, istTeam } from '../../lib/rollen';
import { brevoKampagneUrl } from '../../lib/brevo';
import { useAuthStore } from '../../stores/authStore';
import { useFeedbackStore } from '../../stores/feedbackStore';
import { AnfrageDialog } from './AnfrageDialog';
import { PlanungBearbeiten } from './PlanungBearbeiten';
import { LoeschenDialog } from './LoeschenDialog';
import './GeplanteMail.css';

/** Was die letzte Übertragung nach Brevo ergeben hat (bleibt stehen, bis sie sich erledigt). */
type BrevoMeldung =
  | { art: 'konflikt' }
  | { art: 'eingeplant' }
  | { art: 'kein_entwurf' }
  | { art: 'nicht_offen'; status: string | null }
  | { art: 'angehalten' }
  | { art: 'fehler'; text: string };

type Rueckfrage = 'pull' | 'force' | null;
type Dialog = 'termine' | 'anfrage' | 'loeschen' | null;

const DETAILS_KEY = 'gmd-details';
/** Mailtool-Stände, in denen brevo-draft „pull“ zulässt */
const PULL_STATUS: string[] = ['draft', 'failed', 'pending_approval'];

/** Gemerkte Wahl; sonst auf dem Handy eingeklappt, damit die Mail gleich sichtbar ist. */
function detailsGemerkt(): boolean {
  let wert: string | null = null;
  try { wert = localStorage.getItem(DETAILS_KEY); } catch { wert = null; }
  if (wert === 'auf') return true;
  if (wert === 'zu') return false;
  return !window.matchMedia?.('(max-width: 700px)').matches;
}
function detailsMerken(offen: boolean) {
  try { localStorage.setItem(DETAILS_KEY, offen ? 'auf' : 'zu'); } catch { /* ohne Speicher bleibt es beim Standard */ }
}

/**
 * Läuft in der Werkstatt gerade eine Textänderung? Die Bibliothek setzt im
 * Bearbeitungsmodus data-za-block / contenteditable in die Mail — solange
 * das so ist, lädt die Seite nicht von selbst nach.
 */
function wirdBearbeitet(wurzel: HTMLElement | null): boolean {
  if (!wurzel) return false;
  for (const f of Array.from(wurzel.querySelectorAll('iframe'))) {
    try {
      if (f.contentDocument?.querySelector('[data-za-block], [contenteditable="true"]')) return true;
    } catch {
      // fremder Ursprung — dann ist es nicht unsere Bearbeitung
    }
  }
  return false;
}

function fehlerText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Mail-Werkstatt fürs Team (/geplante-mails/:id): Kopf mit Terminen und
 * Stand, Überblick (Empfänger, Ansprechpersonen, Brevo, Statistik, Notiz)
 * und darunter die Mail mit Kommentaren, Änderungsvorschlägen und Versionen.
 * Was jemand sehen und ändern darf, entscheidet die Datenbank.
 */
export function GeplanteMail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const teamEdit = !!user && !user.ohneRolle && (user.role === 'admin' || user.role === 'redakteur');
  // Admins öffnen jeden Link (Admin-Ansicht, nur lesen — Migration 0052).
  const admin = !!user && !user.ohneRolle && user.role === 'admin';
  const team = istTeam(user);
  const redaktion = istRedaktion(user);

  const [dialog, setDialog] = useState<Dialog>(null);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage>(null);
  const [brevoMeldung, setBrevoMeldung] = useState<BrevoMeldung | null>(null);
  const [brevoArbeitet, setBrevoArbeitet] = useState(false);
  const [detailsOffen, setDetailsOffen] = useState(detailsGemerkt);
  const werkstattRef = useRef<HTMLDivElement>(null);
  const brevoRef = useRef<HTMLDivElement>(null);
  const push = useRef<{ laeuft: boolean; nochmal: boolean; timer: number | null }>({ laeuft: false, nochmal: false, timer: null });

  const abfrage = useQuery({
    queryKey: ['planung-mail', id],
    queryFn: () => ladePlanungMail(id),
    enabled: !!id,
  });
  const mail = abfrage.data ?? undefined;
  const { refetch } = abfrage;

  // Andere Mail geöffnet (gleiche Seite, neue Adresse): Meldungen und Dialoge der vorigen gelten nicht mehr.
  useEffect(() => {
    setBrevoMeldung(null);
    setDialog(null);
    setRueckfrage(null);
    const p = push.current;
    return () => {
      if (p.timer) { window.clearTimeout(p.timer); p.timer = null; }
    };
  }, [id]);

  // Aktuelle Werte für Rückrufe, die stabil bleiben sollen (Adapter der Werkstatt).
  const mailRef = useRef<PlanungsDetail | undefined>(mail);
  const toastRef = useRef(toast);
  useEffect(() => { mailRef.current = mail; }, [mail]);
  useEffect(() => { toastRef.current = toast; });

  // Alle 30 Sekunden nachladen — nicht während einer Textänderung, nicht
  // bei offenem Dialog und nicht im Hintergrund-Tab.
  const pausiert = dialog !== null || rueckfrage !== null;
  useEffect(() => {
    if (!id || pausiert) return;
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || wirdBearbeitet(werkstattRef.current)) return;
      void refetch();
    }, 30_000);
    return () => window.clearInterval(t);
  }, [id, pausiert, refetch]);

  // Beim ersten Öffnen (und wenn Neues dazukommt) gelten die Rückmeldungen als gelesen.
  const gelesenFuer = useRef<string | null>(null);
  const ungelesen = mail?.zaehler.ungelesen ?? 0;
  const geladen = abfrage.isSuccess;
  useEffect(() => {
    if (!teamEdit || !id || !geladen) return;
    if (gelesenFuer.current === id && ungelesen === 0) return;
    gelesenFuer.current = id;
    feedbackGelesen(id)
      .then(() => {
        void qc.invalidateQueries({ queryKey: ['feedback-alle'] });
        void qc.invalidateQueries({ queryKey: ['planung-liste'] });
        return useFeedbackStore.getState().laden();
      })
      .catch(() => { /* Die Zahl am Menüpunkt ist nur ein Hinweis. */ });
  }, [teamEdit, id, geladen, ungelesen, qc]);

  const betreff = mail?.betreff?.trim() || mail?.name || '';
  useEffect(() => {
    if (!betreff) return;
    const vorher = document.title;
    document.title = `${betreff} · Geplante Mails`;
    return () => { document.title = vorher; };
  }, [betreff]);

  const neuLaden = useCallback(async () => {
    await Promise.all([
      refetch(),
      qc.invalidateQueries({ queryKey: ['planung-liste'] }),
      qc.invalidateQueries({ queryKey: ['feedback-alle'] }),
    ]);
  }, [refetch, qc]);

  /** Ergebnis von brevo-draft in Klartext; still = nach dem Speichern, ohne Erfolgsmeldung bei „nichts zu tun“. */
  const ergebnisMelden = useCallback((r: BrevoDraftResult, still: boolean) => {
    const t = toastRef.current;
    switch (r.result) {
      case 'pushed':
        setBrevoMeldung(null);
        t.success(still ? 'Auch der Brevo-Entwurf ist jetzt aktuell.' : 'In Brevo aktualisiert.');
        break;
      case 'pulled':
        setBrevoMeldung(null);
        t.success('Der Stand aus Brevo ist übernommen.');
        break;
      case 'conflict':
        setBrevoMeldung({ art: 'konflikt' });
        setDetailsOffen(true);
        t.warning('Der Entwurf wurde in Brevo geändert — bitte im Kasten „Brevo“ entscheiden, welcher Stand gilt.');
        break;
      case 'not_draft':
        setBrevoMeldung({ art: 'eingeplant' });
        if (!still) {
          // Der Server meldet den echten Stand in Brevo (der gespeicherte kann bis 15 Min. alt sein).
          if (r.brevo_status === 'in_process') t.info('Brevo verschickt die Mail gerade — Änderungen kommen nicht mehr an.');
          else if (r.brevo_status === 'sent' || r.brevo_status === 'archive') t.info('Brevo hat die Mail schon verschickt.');
          else t.info('In Brevo eingeplant — dort erst anhalten, dann aktualisieren.');
        }
        break;
      case 'no_draft':
        setBrevoMeldung({ art: 'kein_entwurf' });
        if (!still) t.info('Noch keine Brevo-Kampagne — sie entsteht mit der ersten Testmail.');
        break;
      case 'not_open':
        setBrevoMeldung({ art: 'nicht_offen', status: r.status ?? r.mailtool_status ?? null });
        if (!still) t.info('Die Mail ist im Mailtool nicht mehr offen — in Brevo ändert sich nichts.');
        break;
      default:
        break;
    }
  }, []);

  // Nach dem Speichern still nach Brevo übertragen. Mehrere Speicherungen
  // kurz hintereinander werden zusammengefasst und nie parallel geschickt —
  // Brevo drosselt schnelle Aufrufe.

  const stillUebertragen = useCallback(async () => {
    const p = push.current;
    if (p.laeuft) { p.nochmal = true; return; }
    p.laeuft = true;
    setBrevoArbeitet(true);
    try {
      let r: BrevoDraftResult;
      do {
        p.nochmal = false;
        r = await nachBrevoUebertragen(id);
      } while (p.nochmal && r.result === 'pushed');
      ergebnisMelden(r, true);
    } catch (err) {
      setBrevoMeldung({ art: 'fehler', text: fehlerText(err) });
      toastRef.current.warning(`Gespeichert — aber Brevo ist nicht aktualisiert: ${fehlerText(err)}`);
    } finally {
      p.laeuft = false;
      setBrevoArbeitet(false);
      void neuLaden();
    }
  }, [id, ergebnisMelden, neuLaden]);

  const brevoNachziehen = useCallback(() => {
    const m = mailRef.current;
    if (!m?.brevo_id || m.status === 'sent' || m.status === 'sending') return;
    if (m.brevo_status === 'suspended') {
      setBrevoMeldung({ art: 'angehalten' });
      toastRef.current.info('Die Mail ist in Brevo angehalten — mit „In Brevo aktualisieren“ kommt die Änderung dorthin.');
      return;
    }
    // Noch kein Status gelesen: brevo-draft prüft selbst und lässt Eingeplantes in Ruhe (not_draft).
    if (m.brevo_status && m.brevo_status !== 'draft') return;
    const p = push.current;
    if (p.timer) window.clearTimeout(p.timer);
    p.timer = window.setTimeout(() => { p.timer = null; void stillUebertragen(); }, 1200);
  }, [stillUebertragen]);

  const ichName = user?.fullName || user?.email || 'Ich';
  // Ablehnen und Erledigen darf das Team; Inhalt ändern (übernehmen, direkt bearbeiten,
  // wiederherstellen) nur, wem die Datenbank es erlaubt — Redakteure z. B. nur bei eigenen Mails.
  const darfInhalt = teamEdit && mail?.darf_inhalt_aendern === true;
  const adapter = useMemo<WerkstattAdapter>(() => {
    const basis: WerkstattAdapter = {
      art: 'team',
      ichName,
      darfEntscheiden: teamEdit,
      istAdmin: admin,
      senden: (daten) => feedbackSenden(id, daten),
      version: (nr) => ladePlanungVersion(id, nr),
      neuLaden,
    };
    if (!teamEdit) return basis;
    const team: WerkstattAdapter = {
      ...basis,
      entscheiden: (feedbackId, ergebnis, index) => feedbackEntscheiden(feedbackId, ergebnis, index),
    };
    if (!darfInhalt) return team;
    return {
      ...team,
      inhaltSpeichern: async (p) => {
        const r = await inhaltAendern({
          campaignId: id, html: p.html, basisMd5: p.basisMd5, quelle: p.quelle, betreff: p.betreff ?? null, notiz: p.notiz ?? null,
        });
        brevoNachziehen();
        return r;
      },
      wiederherstellen: async (nr, basisMd5) => {
        await versionWiederherstellen(id, nr, basisMd5);
        brevoNachziehen();
      },
    };
  }, [id, ichName, teamEdit, admin, darfInhalt, neuLaden, brevoNachziehen]);

  async function inBrevoAktualisieren(force = false) {
    const m = mailRef.current;
    if (!m) return;
    setBrevoArbeitet(true);
    try {
      // Ausdrücklicher Klick: auch eine inzwischen angehaltene Kampagne aktualisieren.
      // Eingeplante (queued) lässt brevo-draft trotzdem in Ruhe.
      ergebnisMelden(await nachBrevoUebertragen(id, true, force), false);
    } catch (err) {
      setBrevoMeldung({ art: 'fehler', text: fehlerText(err) });
      toast.error(`Brevo nicht aktualisiert: ${fehlerText(err)}`);
    } finally {
      setBrevoArbeitet(false);
      await neuLaden();
    }
  }

  async function brevoStandUebernehmen() {
    setBrevoArbeitet(true);
    try {
      ergebnisMelden(await edgeFunctions.brevoDraft(id, 'pull'), false);
    } catch (err) {
      toast.error(`Übernehmen aus Brevo ging nicht: ${fehlerText(err)}`);
    } finally {
      setBrevoArbeitet(false);
      await neuLaden();
    }
  }

  function rueckfrageBestaetigen() {
    const was = rueckfrage;
    setRueckfrage(null);
    if (was === 'pull') void brevoStandUebernehmen();
    if (was === 'force') void inBrevoAktualisieren(true);
  }

  function detailsUmschalten() {
    const neu = !detailsOffen;
    setDetailsOffen(neu);
    detailsMerken(neu);
  }

  // Auf Wunsch zum Brevo-Kasten springen (Kopf-Hinweis „nicht aktuell“).
  function zuBrevo() {
    if (!detailsOffen) { setDetailsOffen(true); detailsMerken(true); }
    window.setTimeout(() => brevoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  }

  // ── Zustände ohne Mail ────────────────────────────────────────────────────
  if (!mail) {
    return (
      <div className="gmd-seite">
        <ZurueckLink />
        {abfrage.isSuccess ? (
          <div className="gmd-fehler card" role="alert">
            <EyeOff size={28} aria-hidden="true" />
            <h1 className="gmd-fehler-titel">Diese Mail gibt es nicht (mehr)</h1>
            <p>Vielleicht wurde sie archiviert oder gelöscht.</p>
            <div className="gmd-fehler-knoepfe">
              <Link to="/geplante-mails" className="btn btn-primary">Zur Übersicht</Link>
            </div>
          </div>
        ) : abfrage.isError ? (
          <div className="gmd-fehler card" role="alert">
            <AlertTriangle size={28} aria-hidden="true" />
            <h1 className="gmd-fehler-titel">Die Mail lässt sich gerade nicht öffnen</h1>
            <p>{fehlerText(abfrage.error)}</p>
            <p className="hint">Vielleicht wurde sie archiviert, oder Du darfst sie nicht sehen. Sonst hilft oft ein zweiter Versuch.</p>
            <div className="gmd-fehler-knoepfe">
              <button type="button" className="btn btn-primary" onClick={() => void refetch()} disabled={abfrage.isFetching}>
                <RefreshCw size={16} aria-hidden="true" className={abfrage.isFetching ? 'spin' : undefined} /> Nochmal versuchen
              </button>
              <Link to="/geplante-mails" className="btn btn-secondary">Zur Übersicht</Link>
            </div>
          </div>
        ) : (
          <div className="gmd-laden" aria-busy="true" aria-live="polite">
            <div className="gmd-skelett gmd-skelett--titel" />
            <div className="gmd-skelett gmd-skelett--zeile" />
            <div className="gmd-skelett-raster">
              <div className="gmd-skelett gmd-skelett--karte" />
              <div className="gmd-skelett gmd-skelett--karte" />
              <div className="gmd-skelett gmd-skelett--karte" />
            </div>
            <p className="gmd-laden-text"><span className="loading-spinner" aria-hidden="true" /> Die Mail wird geladen …</p>
          </div>
        )}
      </div>
    );
  }

  const versendet = mail.status === 'sent';
  // Löschen wie brevo-draft „loeschen“: wer die Mail bearbeiten darf (die Datenbank sagt es), nie versendet.
  const darfLoeschen = teamEdit && mail.darf_loeschen === true && !versendet && mail.status !== 'sending';
  const editorLink = mail.aus_assistent ? `/neue-mail/${mail.id}` : `/campaigns/${mail.id}`;

  return (
    <div className="gmd-seite">
      <ZurueckLink />

      <header className="gmd-kopf">
        <p className="gmd-meta">
          <span className="gmd-name" title="Interner Name der Mail">{mail.name}</span>
          {mail.erstellt_von && <span>von {mail.erstellt_von}</span>}
          {mail.inhalt_geaendert_am && <span title={datumZeit(mail.inhalt_geaendert_am)}>zuletzt geändert {wannText(mail.inhalt_geaendert_am)}</span>}
        </p>
        <h1 className="page-title gmd-titel">{betreff || 'Ohne Betreff'}</h1>

        {(teamEdit || (team && versendet)) && (
          <div className="gmd-aktionen">
            {teamEdit && (
              <button type="button" className="btn btn-secondary" onClick={() => setDialog('termine')}>
                <CalendarClock size={16} aria-hidden="true" /> Termine &amp; Stand
              </button>
            )}
            {teamEdit && !versendet && (
              <button type="button" className="btn btn-primary" onClick={() => setDialog('anfrage')}>
                <UserPlus size={16} aria-hidden="true" /> Feedback anfragen
              </button>
            )}
            {team && versendet && (
              <Link to={`/campaigns/${mail.id}/stats`} className="btn btn-secondary">
                <BarChart3 size={16} aria-hidden="true" /> Statistik
              </Link>
            )}
            {teamEdit && (
              <Link to={editorLink} className="btn btn-ghost">
                <ExternalLink size={16} aria-hidden="true" /> Im {mail.aus_assistent ? 'Assistenten' : 'Editor'} öffnen
              </Link>
            )}
            {darfLoeschen && (
              <button type="button" className="btn btn-ghost gmd-loeschen" onClick={() => setDialog('loeschen')} title="Löschen — lässt sich unter „Gelöscht“ wiederherstellen">
                <Trash2 size={16} aria-hidden="true" /> Löschen
              </button>
            )}
          </div>
        )}

        <div className="gmd-chips">
          <StandBadge stand={mail.stand} />
          <VersandChip mail={mail} />
          <EventChip mail={mail} />
          <EmpfaengerChip mail={mail} />
          {mail.brevo_veraltet && teamEdit ? (
            <button type="button" className="gmd-chip-knopf" onClick={zuBrevo} aria-label="Brevo ist nicht aktuell — zum Kasten Brevo">
              <BrevoChip mail={mail} />
            </button>
          ) : <BrevoChip mail={mail} />}
          <SportschulChips mail={mail} />
          {mail.ausgeblendet && (
            <span className="badge badge-muted" title="Steht nicht in der Übersicht (z. B. Testmail)">
              <EyeOff size={12} aria-hidden="true" /> Ausgeblendet
            </span>
          )}
        </div>
      </header>

      {mail.status === 'pending_approval' && (
        <div className="alert alert-info gmd-hinweis">
          <ShieldCheck size={16} aria-hidden="true" />
          <div>
            <strong>Liegt zur Freigabe bei der Redaktion.</strong> Kommentare und Vorschläge gehen trotzdem —
            Änderungen am Inhalt macht die Redaktion beim Prüfen.
            {redaktion && <> <Link to={`/freigaben?mail=${mail.id}`} className="gmd-link">Zu den Freigaben</Link></>}
          </div>
        </div>
      )}

      <section className="gmd-ueberblick" aria-labelledby="gmd-ueberblick-titel">
        <div className="gmd-ueberblick-kopf">
          <h2 id="gmd-ueberblick-titel" className="section-title">Überblick</h2>
          {!detailsOffen && <Kurzfassung mail={mail} />}
          <button
            type="button"
            className="btn btn-ghost btn-sm gmd-einklappen"
            aria-expanded={detailsOffen}
            aria-controls="gmd-ueberblick-inhalt"
            onClick={detailsUmschalten}
          >
            {detailsOffen ? 'Einklappen' : 'Aufklappen'}
            <ChevronDown size={14} aria-hidden="true" className={detailsOffen ? 'gmd-dreh' : undefined} />
          </button>
        </div>

        {detailsOffen && (
          <div id="gmd-ueberblick-inhalt" className="gmd-ueberblick-inhalt">
            {versendet && <StatistikBand mail={mail} team={team} />}
            <div className="gmd-raster">
              <TermineKarte mail={mail} darf={teamEdit} onAendern={() => setDialog('termine')} />
              <EmpfaengerKarte mail={mail} />
              <PersonenKarte mail={mail} darf={teamEdit && !versendet} team={teamEdit} admin={admin} onAnfragen={() => setDialog('anfrage')} />
              <div ref={brevoRef} className="gmd-raster-zelle">
                <BrevoKarte
                  mail={mail}
                  darf={teamEdit}
                  zeigeLink={team}
                  arbeitet={brevoArbeitet}
                  meldung={brevoMeldung}
                  onAktualisieren={() => void inBrevoAktualisieren()}
                  onPull={() => setRueckfrage('pull')}
                  onForce={() => setRueckfrage('force')}
                />
              </div>
              {mail.notiz && <NotizKarte notiz={mail.notiz} darf={teamEdit} onAendern={() => setDialog('termine')} />}
            </div>
          </div>
        )}
      </section>

      <div ref={werkstattRef} className="gmd-werkstatt">
        <MailWerkstatt key={mail.id} mail={mail} adapter={adapter} />
      </div>

      {teamEdit && (
        <>
          <PlanungBearbeiten mail={mail} offen={dialog === 'termine'} onClose={() => setDialog(null)} onGespeichert={() => void neuLaden()} />
          <AnfrageDialog mail={mail} offen={dialog === 'anfrage'} onClose={() => setDialog(null)} onGeaendert={() => void neuLaden()} />
          {darfLoeschen && (
            <LoeschenDialog
              mail={mail}
              offen={dialog === 'loeschen'}
              onClose={() => setDialog(null)}
              onGeloescht={() => navigate('/geplante-mails', { replace: true })}
            />
          )}
          <Modal
            isOpen={rueckfrage !== null}
            onClose={() => setRueckfrage(null)}
            title={rueckfrage === 'pull' ? 'Brevo-Stand übernehmen?' : 'Mailtool-Stand übertragen?'}
            size="sm"
          >
            <div className="gmd-rueckfrage">
              {rueckfrage === 'pull' ? (
                <>
                  <p>Text, Betreff, Vorschautext und Absender aus Brevo ersetzen den Stand im Mailtool. Die jetzige Fassung bleibt in den Versionen und lässt sich wiederherstellen.</p>
                  {mail.aus_assistent && <p className="gmd-rueckfrage-warnung">Die Mail kommt aus dem Assistenten: Speichert dort jemand, gilt wieder der Stand des Assistenten.</p>}
                </>
              ) : (
                <p>Was in Brevo geändert wurde, geht dabei verloren. Danach steht dort wieder genau der Stand aus dem Mailtool.</p>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setRueckfrage(null)}>Abbrechen</button>
                <button type="button" className={`btn ${rueckfrage === 'force' ? 'btn-danger' : 'btn-primary'}`} onClick={rueckfrageBestaetigen} autoFocus>
                  {rueckfrage === 'pull' ? <><Download size={16} aria-hidden="true" /> Übernehmen</> : <><Upload size={16} aria-hidden="true" /> Trotzdem übertragen</>}
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}

/** Eingeklappt: das Wichtigste in einer Zeile. */
function Kurzfassung({ mail }: { mail: PlanungsDetail }) {
  const offen = mail.anfragen.filter((a) => a.antwort === 'offen').length;
  const teile: { text: string; warn?: boolean }[] = [{ text: empfaengerText(mail.empfaenger) }];
  if (mail.anfragen.length) teile.push({ text: offen ? `${offen} von ${mail.anfragen.length} Antworten offen` : 'alle Ansprechpersonen haben geantwortet' });
  if (mail.brevo_veraltet && mail.status !== 'sent') teile.push({ text: 'Brevo nicht aktuell', warn: true });
  if (mail.notiz) teile.push({ text: 'mit Notiz' });
  return (
    <p className="gmd-kurz">
      {teile.map((t, i) => (
        <Fragment key={t.text}>
          {i > 0 && ' '}
          <span className={t.warn ? 'gmd-kurz-warn' : undefined}>{t.text}</span>
        </Fragment>
      ))}
    </p>
  );
}

function ZurueckLink() {
  return (
    <div className="back-row gmd-zurueck">
      <Link to="/geplante-mails" className="back-btn"><ChevronLeft size={15} aria-hidden="true" /> Alle geplanten Mails</Link>
    </div>
  );
}

function KartenKopf({ icon: Icon, titel, zusatz }: { icon: LucideIcon; titel: string; zusatz?: React.ReactNode }) {
  return (
    <div className="gmd-karte-kopf">
      <span className="gmd-karte-symbol" aria-hidden="true"><Icon size={16} /></span>
      <h3 className="gmd-karte-titel">{titel}</h3>
      {zusatz && <span className="gmd-karte-zusatz">{zusatz}</span>}
    </div>
  );
}

function Fakt({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: React.ReactNode }) {
  return (
    <li className="gmd-fakt">
      <span className="gmd-fakt-symbol" aria-hidden="true"><Icon size={15} /></span>
      <span className="gmd-fakt-text">
        <span className="gmd-fakt-label">{label}</span>
        <span className="gmd-fakt-wert">{children}</span>
      </span>
    </li>
  );
}

/** Versand, Event, „Feedback bis“ und Stand. */
function TermineKarte({ mail, darf, onAendern }: { mail: PlanungsDetail; darf: boolean; onAendern: () => void }) {
  const { am, art } = mail.versand;
  const bis = mail.feedback_bis;
  const bisTag = alsTag(bis);
  const bisVorbei = !!bisTag && bisTag < alsTag(new Date())!;
  return (
    <article className="gmd-karte">
      <KartenKopf icon={CalendarDays} titel="Termine" zusatz={darf ? <button type="button" className="gmd-mini-knopf" onClick={onAendern}>Ändern</button> : undefined} />
      <ul className="gmd-fakten">
        <Fakt icon={Send} label="Versand">
          {am && art ? (
            <>
              <strong>{datumZeit(am)}</strong>
              <span className="gmd-fakt-unter">
                {VERSAND_INFO[art].label}{art !== 'versendet' && tageText(am) ? ` · ${tageText(am)}` : ''}
              </span>
            </>
          ) : <span className="gmd-leer">Noch kein Termin</span>}
        </Fakt>
        <Fakt icon={CalendarDays} label="Event">
          {mail.event.beginn ? (
            <>
              <strong>{zeitraumText(mail.event.beginn, mail.event.ende)}</strong>
              {mail.event.titel && <span className="gmd-fakt-unter">{mail.event.titel}</span>}
            </>
          ) : <span className="gmd-leer">Kein Event erkannt</span>}
        </Fakt>
        {art !== 'versendet' && (
          <Fakt icon={Hourglass} label="Feedback bis">
            {bis ? (
              <>
                <strong className={bisVorbei && mail.stand === 'feedback' ? 'gmd-warn-text' : undefined}>{datumKurz(bis)}</strong>
                <span className="gmd-fakt-unter">{tageText(bis)}</span>
              </>
            ) : <span className="gmd-leer">Ohne Frist</span>}
          </Fakt>
        )}
      </ul>
      {art !== 'versendet' && <p className="gmd-stand-text">{STAND_INFO[mail.stand].text}</p>}
    </article>
  );
}

/** Zielgruppen mit Anzahl und die interne Kopie — nur Namen und Zahlen, nie Adressen. */
function EmpfaengerKarte({ mail }: { mail: PlanungsDetail }) {
  const e = mail.empfaenger;
  const gesamt = e.summe + e.manuell;
  return (
    <article className="gmd-karte">
      <KartenKopf icon={Users} titel="Empfängerkreis" />
      <p className="gmd-gross">
        <strong>{gesamt ? (e.mehrere ? 'bis zu ' : '') + gesamt.toLocaleString('de-DE') : '0'}</strong>
        <span>{gesamt === 1 ? 'Postfach' : 'Postfächer'}</span>
      </p>
      {e.gruppen.length > 0 ? (
        <ul className="gmd-gruppen">
          {e.gruppen.map((g) => (
            <li key={g.name}>
              <span className="gmd-gruppe-name">{g.name}</span>
              <span className="gmd-gruppe-zahl">{g.anzahl.toLocaleString('de-DE')}</span>
            </li>
          ))}
          {e.manuell > 0 && (
            <li>
              <span className="gmd-gruppe-name">Einzeln eingetragen</span>
              <span className="gmd-gruppe-zahl">{e.manuell.toLocaleString('de-DE')}</span>
            </li>
          )}
        </ul>
      ) : (
        <p className="gmd-leer">{e.manuell > 0 ? `${e.manuell} einzeln eingetragen, keine Zielgruppe` : 'Noch keine Zielgruppe gewählt.'}</p>
      )}
      {e.mehrere && <p className="gmd-klein">Wer in mehreren Gruppen steht, bekommt die Mail nur einmal.</p>}
      {e.intern.length > 0 && (
        <div className="gmd-intern">
          <span className="gmd-intern-titel">Interne Kopie</span>
          <span className="gmd-intern-liste">{e.intern.map((n) => <span key={n} className="gmd-intern-name">{n}</span>)}</span>
        </div>
      )}
    </article>
  );
}

/** Warum sieht eine Person die Mail über ihren Link? */
function grundText(s: SichtbarFuer, mail: PlanungsDetail): string {
  if (s.grund === 'alle') return 'sieht alle Mails';
  // Die Datenbank liefert die Sportschulen, über die die Person die Mail sieht; sonst die der Mail.
  const schulen = s.sportschulen?.length ? s.sportschulen : mail.sportschulen;
  return schulen.length ? `über Sportschule ${schulen.join(', ')}` : 'über ihre Sportschule';
}

/** Knöpfe einer Person: Link zu dieser Mail kopieren, für Admins „Öffnen“ (Admin-Ansicht im neuen Tab). */
function PersonKnoepfe({ name, token, mailId, teilen, admin, kopiert, onKopieren }: {
  name: string; token: string | null | undefined; mailId: string; teilen: boolean; admin: boolean; kopiert: boolean; onKopieren: () => void;
}) {
  if (!token || (!teilen && !admin)) return null;
  return (
    <span className="gmd-person-knoepfe">
      {teilen && (
        <button
          type="button"
          className="action-btn gmd-kopieren"
          onClick={onKopieren}
          aria-label={`Persönlichen Link für ${name} zu dieser Mail kopieren`}
          title="Link kopieren"
        >
          {kopiert ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
        </button>
      )}
      {admin && (
        <a
          className="action-btn gmd-kopieren"
          href={feedbackLink(token, mailId)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Mail so öffnen, wie ${name} sie sieht (Admin-Ansicht, neuer Tab)`}
          title="Öffnen — so sieht die Person die Mail (nur ansehen)"
        >
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      )}
    </span>
  );
}

/**
 * Wer ist gefragt, was hat er/sie geantwortet, wann zuletzt reingeschaut?
 * Darunter alle, die die Mail sonst über ihren Link sehen (Sportschule,
 * „alle Mails“) — mit Link zu dieser Mail; Admins öffnen jeden Link.
 */
function PersonenKarte({ mail, darf, team, admin, onAnfragen }: {
  /** darf = fragen und Links teilen (Team, Mail nicht versendet); team = „zuletzt da“ und Hinweis auf den Reiter; admin = jeden Link öffnen */
  mail: PlanungsDetail; darf: boolean; team: boolean; admin: boolean; onAnfragen: () => void;
}) {
  const toast = useToast();
  const [kopiert, setKopiert] = useState<string | null>(null);
  const offen = mail.anfragen.filter((a) => a.antwort === 'offen').length;
  // Angefragte stehen schon oben — hier nur, wer die Mail über Sportschule oder „alle Mails“ sieht.
  const weitere = (mail.sichtbar_fuer ?? []).filter((s) => s.grund !== 'anfrage');
  const leer = mail.anfragen.length === 0 && weitere.length === 0;
  // Ausgeblendete (und gelöschte) Mails zeigt kein Link — auch nicht den Angefragten (mail_person_sieht_mail).
  const ueberLink = !mail.ausgeblendet && mail.status !== 'archived';

  async function kopieren(personId: string, name: string, token: string | null | undefined, sonst: string) {
    if (!token) return;
    if (await inZwischenablage(feedbackLink(token, mail.id))) {
      setKopiert(personId);
      window.setTimeout(() => setKopiert((k) => (k === personId ? null : k)), 2000);
      toast.success(`Link für ${name} kopiert`);
    } else {
      toast.error(`Kopieren ging nicht — ${sonst}`);
    }
  }

  return (
    <article className="gmd-karte">
      <KartenKopf
        icon={MessagesSquare}
        titel="Ansprechpersonen"
        zusatz={mail.anfragen.length > 0
          ? <span className={offen ? 'gmd-zusatz-warn' : 'gmd-zusatz-ok'}>{offen ? `${offen} offen` : 'alle geantwortet'}</span>
          : undefined}
      />
      {leer ? (
        <div className="gmd-leer-block">
          <p className="gmd-leer">Noch niemand um Feedback gebeten.</p>
          {darf && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={onAnfragen}>
              <UserPlus size={14} aria-hidden="true" /> Jemanden fragen
            </button>
          )}
        </div>
      ) : mail.anfragen.length === 0 ? (
        <div className="gmd-leer-zeile">
          <p className="gmd-leer">Um Feedback gefragt: noch niemand.</p>
          {darf && (
            <button type="button" className="gmd-mini-knopf" onClick={onAnfragen}>
              <UserPlus size={13} aria-hidden="true" /> Jemanden fragen
            </button>
          )}
        </div>
      ) : (
        <ul className="gmd-personen">
          {mail.anfragen.map((a) => {
            const info = ANTWORT_INFO[a.antwort];
            return (
              <li key={a.person_id} className="gmd-person">
                <Avatar name={a.name} groesse="sm" />
                <div className="gmd-person-text">
                  <span className="gmd-person-zeile">
                    <span className="gmd-person-name">{a.name}</span>
                    <span className={`badge ${info.badge}`}>{info.label}</span>
                  </span>
                  <span className="gmd-person-unter" title={a.gesehen_am ? datumZeit(a.gesehen_am) : undefined}>
                    {a.rolle_text && <span className="gmd-person-rolle">{a.rolle_text}</span>}
                    {a.gesehen_am ? `zuletzt geöffnet ${wannText(a.gesehen_am)}` : a.eingeladen_am ? `eingeladen ${wannText(a.eingeladen_am)}, noch nicht geöffnet` : 'noch nicht geöffnet'}
                  </span>
                </div>
                {a.aktiv === false ? (
                  <span className="badge badge-muted gmd-gesperrt" title="Unter „Ansprechpersonen“ wieder freigeben">Link gesperrt</span>
                ) : !ueberLink ? (
                  <span className="badge badge-muted gmd-gesperrt" title={`${mail.ausgeblendet ? 'Ausgeblendet' : 'Gelöscht'} — über den Link nicht sichtbar`}>
                    Nicht sichtbar
                  </span>
                ) : (
                  <PersonKnoepfe
                    name={a.name} token={a.token} mailId={mail.id} teilen={darf} admin={admin}
                    kopiert={kopiert === a.person_id}
                    onKopieren={() => void kopieren(a.person_id, a.name, a.token, 'öffne „Feedback anfragen“, dort steht der Link.')}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!ueberLink && mail.anfragen.length > 0 && (
        <p className="gmd-klein gmd-personen-aus">
          <EyeOff size={12} aria-hidden="true" />
          {mail.ausgeblendet
            ? 'Ausgeblendet — über den Link nicht sichtbar. Einblenden unter „Termine & Stand“.'
            : 'Gelöscht — über den Link nicht sichtbar.'}
        </p>
      )}
      {darf && mail.anfragen.length > 0 && (
        <button type="button" className="gmd-mini-knopf gmd-mini-knopf--unten" onClick={onAnfragen}>
          <UserPlus size={13} aria-hidden="true" /> Weitere fragen, Links teilen
        </button>
      )}

      {weitere.length > 0 && (
        <div className="gmd-sieht">
          <h4 className="gmd-sieht-titel">Sieht die Mail auch <span className="gmd-sieht-zahl">{weitere.length}</span></h4>
          <ul className="gmd-personen">
            {weitere.map((s, i) => (
              <li key={s.person_id ?? `${s.name}-${i}`} className="gmd-person gmd-person--sieht">
                <Avatar name={s.name} groesse="sm" />
                <span className="gmd-person-zeile gmd-person-kopf">
                  <span className="gmd-person-name">{s.name}</span>
                  {s.herkunft === 'studioaccount' && (
                    <span className="badge badge-info" title="Kommt automatisch aus dem CRM-Studioaccount">
                      <Building2 size={11} aria-hidden="true" /> Studioaccount
                    </span>
                  )}
                </span>
                <PersonKnoepfe
                  name={s.name} token={s.token} mailId={mail.id} teilen={darf} admin={admin}
                  kopiert={!!s.person_id && kopiert === s.person_id}
                  onKopieren={() => void kopieren(
                    s.person_id ?? s.name, s.name, s.token,
                    `nimm den Übersichts-Link von ${s.name} im Reiter Ansprechpersonen, dort steht diese Mail auch.`,
                  )}
                />
                <span className="gmd-person-info">
                  <span className="gmd-grund" title={s.grund === 'alle' ? 'Sieht über den eigenen Link alle geplanten Mails' : 'Sieht über den eigenen Link alle Mails ihrer Sportschule'}>
                    {s.grund === 'alle' ? <Globe size={12} aria-hidden="true" /> : <School size={12} aria-hidden="true" />}
                    <span>{grundText(s, mail)}</span>
                  </span>
                  {team ? (
                    <span className="gmd-person-unter" title={s.zuletzt_gesehen ? datumZeit(s.zuletzt_gesehen) : undefined}>
                      {s.rolle_text && <span className="gmd-person-rolle">{s.rolle_text}</span>}
                      {s.zuletzt_gesehen ? `zuletzt da ${wannText(s.zuletzt_gesehen)}` : 'Link noch nicht geöffnet'}
                    </span>
                  ) : s.rolle_text && (
                    // Ohne Team-Rechte liefert die Datenbank nicht, wann die Person zuletzt da war.
                    <span className="gmd-person-unter">{s.rolle_text}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {team && (
        <p className="gmd-klein gmd-personen-tipp">
          Alle Personen und ihre Übersichts-Links: <Link to="/geplante-mails?reiter=personen" className="gmd-link">Reiter Ansprechpersonen</Link>
        </p>
      )}
    </article>
  );
}

function BrevoKarte({ mail, darf, zeigeLink, arbeitet, meldung, onAktualisieren, onPull, onForce }: {
  mail: PlanungsDetail; darf: boolean; zeigeLink: boolean; arbeitet: boolean; meldung: BrevoMeldung | null;
  onAktualisieren: () => void; onPull: () => void; onForce: () => void;
}) {
  const s = mail.brevo_status;
  const brevoUrl = brevoKampagneUrl(mail.brevo_id, mail.status, s);
  const abgeschlossen = mail.status === 'sent' || mail.status === 'sending' || s === 'sent' || s === 'in_process';
  const ton = !mail.brevo_id ? 'leer' : s === 'suspended' ? 'warn' : s === 'queued' || s === 'in_process' ? 'info' : s === 'sent' ? 'ok' : 'neutral';
  const eingeplant = s === 'queued';
  const kannAktualisieren = darf && !!mail.brevo_id && !abgeschlossen;
  // Übernehmen aus Brevo geht nur bei einem Brevo-Entwurf, solange die Mail im Mailtool nicht eingeplant ist.
  const kannPull = PULL_STATUS.includes(mail.status) && s === 'draft';

  return (
    <article className="gmd-karte">
      <KartenKopf icon={Mail} titel="Brevo" zusatz={arbeitet ? <span className="gmd-arbeitet"><Loader2 size={13} className="spin" aria-hidden="true" /> überträgt …</span> : undefined} />
      <p className={`gmd-brevo-status gmd-brevo-status--${ton}`}>
        <span className="gmd-punkt" aria-hidden="true" />
        {mail.brevo_id ? brevoStatusText(s) : 'Noch keine Brevo-Kampagne'}
      </p>
      {mail.brevo_id ? (
        <dl className="gmd-brevo-fakten">
          <div>
            <dt>Kampagne</dt>
            <dd>
              {zeigeLink && brevoUrl ? (
                <a href={brevoUrl} target="_blank" rel="noopener noreferrer" className="gmd-link"
                   title="Öffnet diese Kampagne direkt in Brevo (neuer Tab) — falls der Abgleich einmal nicht stimmt">
                  #{mail.brevo_id} in Brevo öffnen <ExternalLink size={12} aria-hidden="true" />
                </a>
              ) : <>#{mail.brevo_id}</>}
            </dd>
          </div>
          <div>
            <dt>Übertragen</dt>
            <dd title={mail.brevo_synced_at ? datumZeit(mail.brevo_synced_at) : undefined}>{mail.brevo_synced_at ? wannText(mail.brevo_synced_at) : 'noch nie'}</dd>
          </div>
          {mail.brevo_status_at && (
            <div><dt>Status gelesen</dt><dd title={datumZeit(mail.brevo_status_at)}>{wannText(mail.brevo_status_at)}</dd></div>
          )}
        </dl>
      ) : (
        <p className="gmd-klein">Sie entsteht mit der ersten Testmail.</p>
      )}

      {mail.brevo_veraltet && !abgeschlossen && meldung?.art !== 'konflikt' && (
        <div className="gmd-brevo-hinweis gmd-brevo-hinweis--warn" role="status">
          <AlertTriangle size={15} aria-hidden="true" />
          <div>
            <p>Im Mailtool gibt es Änderungen, die noch nicht in Brevo sind.</p>
            {eingeplant ? (
              <p className="gmd-klein">In Brevo eingeplant — dort erst anhalten, dann aktualisieren.</p>
            ) : kannAktualisieren && (
              <button type="button" className="btn btn-primary btn-sm" onClick={onAktualisieren} disabled={arbeitet}>
                <Upload size={14} aria-hidden="true" /> In Brevo aktualisieren
              </button>
            )}
            {s === 'suspended' && kannAktualisieren && <p className="gmd-klein">Zeitplan und Status in Brevo bleiben, wie sie sind.</p>}
            {!darf && <p className="gmd-klein">Die Redaktion überträgt sie, bevor die Mail rausgeht.</p>}
          </div>
        </div>
      )}

      {meldung && (
        <BrevoMeldungZeile
          meldung={meldung} darf={darf} arbeitet={arbeitet} kannPull={kannPull} angehalten={s === 'suspended'}
          onPull={onPull} onForce={onForce} veraltet={mail.brevo_veraltet}
        />
      )}
    </article>
  );
}

function BrevoMeldungZeile({ meldung, darf, arbeitet, kannPull, angehalten, veraltet, onPull, onForce }: {
  meldung: BrevoMeldung; darf: boolean; arbeitet: boolean; kannPull: boolean; angehalten: boolean; veraltet: boolean;
  onPull: () => void; onForce: () => void;
}) {
  switch (meldung.art) {
    case 'konflikt':
      return (
        <div className="gmd-brevo-hinweis gmd-brevo-hinweis--warn" role="alert">
          <AlertTriangle size={15} aria-hidden="true" />
          <div>
            <p><strong>{angehalten ? 'Die Mail wurde in Brevo geändert.' : 'Der Entwurf wurde in Brevo geändert.'}</strong> Damit nichts verloren geht, überträgt das Mailtool nichts, bis feststeht, welcher Stand gilt.</p>
            {darf && !kannPull && (
              <p className="gmd-klein">
                {angehalten ? 'Die Mail ist in Brevo angehalten, der Stand von dort lässt sich nicht übernehmen.' : 'Der Stand aus Brevo lässt sich hier nicht übernehmen.'}
                {' '}Änderungen, die in Brevo gemacht wurden, bitte von Hand ins Mailtool übertragen — sonst gehen sie verloren.
              </p>
            )}
            {darf && (
              <div className="gmd-brevo-knoepfe">
                {kannPull && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={onPull} disabled={arbeitet}>
                    <Download size={14} aria-hidden="true" /> Brevo-Stand übernehmen
                  </button>
                )}
                <button type="button" className={`btn ${kannPull ? 'btn-ghost' : 'btn-secondary'} btn-sm`} onClick={onForce} disabled={arbeitet}>
                  <Upload size={14} aria-hidden="true" /> Mailtool-Stand trotzdem übertragen
                </button>
              </div>
            )}
          </div>
        </div>
      );
    case 'eingeplant':
      return veraltet ? null : <MeldungInfo text="In Brevo eingeplant — dort erst anhalten, dann aktualisieren." />;
    case 'kein_entwurf':
      return <MeldungInfo text="Noch keine Brevo-Kampagne — sie entsteht mit der ersten Testmail." />;
    case 'nicht_offen':
      return <MeldungInfo text={`Die Mail ist im Mailtool nicht mehr offen${meldung.status ? ` (${meldung.status})` : ''} — in Brevo ändert sich nichts.`} />;
    case 'angehalten':
      return veraltet ? null : <MeldungInfo text="In Brevo angehalten — Änderungen kommen mit „In Brevo aktualisieren“ dorthin." />;
    case 'fehler':
      return (
        <div className="gmd-brevo-hinweis gmd-brevo-hinweis--fehler" role="alert">
          <AlertTriangle size={15} aria-hidden="true" />
          <p>Brevo nicht aktualisiert: {meldung.text}</p>
        </div>
      );
  }
}

function MeldungInfo({ text }: { text: string }) {
  return (
    <div className="gmd-brevo-hinweis gmd-brevo-hinweis--info" role="status">
      <Info size={15} aria-hidden="true" />
      <p>{text}</p>
    </div>
  );
}

/** Zahlen einer versendeten Mail, groß. */
function StatistikBand({ mail, team }: { mail: PlanungsDetail; team: boolean }) {
  const s = mail.statistik;
  if (!s) {
    return (
      <div className="gmd-statistik gmd-statistik--leer">
        <BarChart3 size={16} aria-hidden="true" /> <StatistikZeile mail={mail} />
      </div>
    );
  }
  const basis = s.zugestellt || s.gesendet;
  const kacheln = [
    { label: 'Versendet', wert: s.gesendet.toLocaleString('de-DE'), unter: 'Postfächer' },
    { label: 'Zugestellt', wert: prozent(s.zugestellt, s.gesendet), unter: s.zugestellt.toLocaleString('de-DE') },
    { label: 'Geöffnet', wert: prozent(s.geoeffnet, basis), unter: s.geoeffnet.toLocaleString('de-DE') },
    { label: 'Geklickt', wert: prozent(s.geklickt, basis), unter: s.geklickt.toLocaleString('de-DE') },
    { label: 'Abgemeldet', wert: s.abgemeldet.toLocaleString('de-DE'), unter: s.bounces ? `${s.bounces.toLocaleString('de-DE')} nicht zustellbar` : 'Abmeldungen' },
  ];
  return (
    <div className="gmd-statistik">
      <ul className="gmd-kpis" aria-label="Statistik der versendeten Mail">
        {kacheln.map((k) => (
          <li key={k.label} className="gmd-kpi">
            <span className="gmd-kpi-label">{k.label}</span>
            <span className="gmd-kpi-wert">{k.wert}</span>
            <span className="gmd-kpi-unter">{k.unter}</span>
          </li>
        ))}
      </ul>
      <p className="gmd-statistik-fuss">
        Stand {datumZeit(s.stand)}
        {team && <> · <Link to={`/campaigns/${mail.id}/stats`} className="gmd-link">Ganze Statistik</Link></>}
      </p>
    </div>
  );
}

function NotizKarte({ notiz, darf, onAendern }: { notiz: string; darf: boolean; onAendern: () => void }) {
  return (
    <article className="gmd-notizleiste">
      <span className="gmd-karte-symbol" aria-hidden="true"><StickyNote size={16} /></span>
      <div className="gmd-notizleiste-text">
        <h3 className="gmd-karte-titel">Interne Notiz <span className="gmd-notiz-hinweis">nur intern, Ansprechpersonen sehen sie nicht</span></h3>
        <p className="gmd-notiz">{notiz}</p>
      </div>
      {darf && <button type="button" className="gmd-mini-knopf" onClick={onAendern}>Ändern</button>}
    </article>
  );
}
